<template>
  <div
    class="mb-2 flex h-9 w-full cursor-pointer items-center rounded-md px-4 py-1 font-medium text-surface-700 transition-colors duration-200 hover:bg-surface-100"
    data-test="project-item"
    @click="navigateTo"
    @mouseenter.stop="isHovering = true"
    @mouseleave.stop="isHovering = false"
  >
    <div class="flex grow items-center gap-2 truncate text-surface-700">
      <span
        v-if="project.attributes.skip_customer"
        class="template-icon-container-small shrink-0"
      >
        <FontAwesomeIcon
          class="text-sm text-gray-500"
          :icon="['far', 'memo']"
        />
      </span>

      <avatar
        v-else
        class="shrink-0"
        size="25"
        :src="projectAvatarUrl"
        :username="projectAvatarName"
      />

      <div class="ml-1 shrink truncate">
        {{ project.attributes.name }}
      </div>

      <div
        v-if="!project.attributes.skip_customer && !project.attributes.activated"
        class="tag ml-2 shrink-0 rounded-full"
      >
        {{ $t('project.labels.deactivated') }}
      </div>
    </div>

    <PButton
      v-show="isHovering"
      class="shrink-0"
      rounded
      severity="secondary"
      style="padding: 0.5rem !important"
      text
      @click.stop="toggle"
    >
      <FontAwesomeIcon icon="ellipsis" />
    </PButton>

    <TieredMenu
      id="overlay_tmenu"
      ref="menu"
      appendTo="#project-sidebar"
      :model="overlayActions"
      popup
    >
      <template #item="{ item, props }">
        <a
          v-ripple
          class="align-items-center flex"
          v-bind="props.action"
          @click.stop="item.action"
        >
          {{ item.label }}
        </a>
      </template>
    </TieredMenu>
  </div>

  <b-modal v-model="is_delete_modal">
    <Modal
      :is_confirmed="project.attributes.name.toLowerCase() === level_name.toLowerCase()"
      :is_small="false"
      @actionPrimary="deleteProject"
    >
      <template #title> {{ $t('modal.labels.removeTitle', { record_name: project.attributes.name }) }}? </template>
      <template #notification_danger>
        <div class="flex items-center">
          <div class="icon mr-2">
            <FontAwesomeIcon
              icon="triangle-exclamation"
              size="2x"
            />
          </div>
          <span>{{ $t('levels.labels.deleteWarning') }}</span>
        </div>
      </template>
      <template #content>
        <div
          class="field"
          :data-delete-string="project.attributes.name"
          data-test="delete-input-confirm"
        >
          <label
            class="label"
            v-html="$t('decision_type.labels.writeEmployeeNameReject', { employeeName: project.attributes.name })"
          />
          <input
            v-model.trim="level_name"
            class="input"
            data-test="input-delete-confirm"
            type="text"
          />
        </div>
      </template>
      <template #ButtonDestructive>
        {{ $t('levels.actions.delete') }}
      </template>
    </Modal>
  </b-modal>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { Assignment, PROFILE_TYPES } from '@/app/data/model_constants';
import { ROUTES_EMPLOYEES } from '@/app/data/route_constants';
import { useI18n } from 'vue-i18n';
import { useToasts } from '@/app/util/toasts';

const { toast_action_failed, toast_action_succeeded } = useToasts();

const { t } = useI18n();

const props = defineProps({
  project: { type: Object, required: true },
  is_following_project: { type: Boolean },
  assignments: { type: Object, default: null },
  is_template: { type: Boolean },
  toggled_project_id: { type: String, default: null },
});

const emit = defineEmits(['toggleProjectId', 'unfollow', 'follow', 'closeProjectSidebar']);

const isHovering = ref(false);
const is_delete_modal = ref(false);
const level_name = ref('');

const store = useStore();
const router = useRouter();

const current_profile = computed(() => store.getters.current_profile);
const current_project = computed(() => store.getters.current_project);

const assignment = computed(() => {
  const attributes = {
    assigned_id: props.project.id,
    assigned_type: props.project.type,
    assignee_id: current_profile.value.id,
    assignee_type: current_profile.value.type,
    assignment_type: Assignment.TYPE_FOLLOW,
  };
  const assignments = _.filter(props.assignments, { attributes });
  return assignments[0] || null;
});

const projectAvatarUrl = computed(() => {
  const attributes = props.project.attributes;

  if (attributes.picture_login_url_s) return attributes.picture_login_url_s;

  if (attributes.skip_unit) {
    return attributes.customer_profile?.avatar || null;
  }

  return null;
});

const projectAvatarName = computed(() => props.project.attributes.name);

const menu = ref(null);

watch(
  () => props.toggled_project_id,
  (newVal) => {
    if (newVal !== props.project.id) {
      menu.value?.hide();
    }
  },
);

function toggleFollow() {
  if (assignment.value) {
    emit('unfollow', assignment.value);
  } else {
    const assign = {
      attributes: {
        assigned_id: props.project.id,
        assigned_type: props.project.type,
        assignee_id: current_profile.value.id,
        assignee_type: current_profile.value.type,
        project_id: props.project.id,
        assignment_type: Assignment.TYPE_FOLLOW,
      },
    };
    if (current_profile.value.type === PROFILE_TYPES.PARTNER) {
      assign.attributes.client_id = props.project.attributes.client_id;
    }
    emit('follow', assign);
  }
}

function toggle(event) {
  if (!props.project?.id) return;
  emit('toggleProjectId', props.project.id);
  menu.value?.toggle(event);
}

async function toggleActivation() {
  try {
    await store.dispatch('UPDATE_NON_CURRENT_PROJECT', {
      record: {
        id: props.project.id,
        attributes: { activated: !props.project.attributes.activated },
      },
      purge: false,
    });
    const i18n = props.project.attributes.activated
      ? 'project.toasts.activateSuccess'
      : 'project.toasts.deactivateSuccess';
    toast_action_succeeded(t(i18n, { project_name: props.project.attributes.name }));
    if (current_project.value?.id === props.project.id) {
      router.go();
    }
  } catch (e) {
    toast_action_failed({ error: e });
  }
}

function navigateTo() {
  const { id, type } = props.project;
  emit('closeProjectSidebar');
  router.push({
    name: ROUTES_EMPLOYEES.PROJECT_INFORMATION_LEVELS_INFORMATION,
    params: {
      project_id: id,
      level_type: type,
      level_id: id,
    },
  });
}

function toggleDeletion() {
  is_delete_modal.value = true;
}

async function deleteProject() {
  await store.dispatch('DELETE_PROJECT', { record: props.project });
  router.push({ name: ROUTES_EMPLOYEES.CLIENT_DASHBOARD });
}

const overlayActions = computed(() => {
  const actions = [];
  if (!props.is_template) {
    actions.push({
      label: props.is_following_project ? t('project.actions.unFollow') : t('project.actions.follow'),
      action: toggleFollow,
    });
    if (props.project.attributes.rundle) {
      actions.push({
        label: props.project.attributes.activated ? t('project.actions.deactivate') : t('project.actions.activate'),
        action: toggleActivation,
      });
    }
  } else {
    actions.push({
      label: t('levels.actions.delete'),
      action: toggleDeletion,
    });
  }
  return actions;
});
</script>

<style scoped>
.template-icon-container {
  align-items: center;
  background-color: #e8e8e8;
  border-radius: 50%;
  display: inline-flex;
  height: 25px;
  justify-content: center;
  width: 25px;
}
</style>
